import HeroSection, { HeroContent } from '../../components/feature/sections/HeroSection';
import TrustBar from '../../components/feature/sections/TrustBar';
import ContentSection, { ContentSectionData } from '../../components/feature/sections/ContentSection';
import AIAnswerBlock, { AIAnswerContent } from '../../components/feature/sections/AIAnswerBlock';
import ProcessSection, { ProcessContent } from '../../components/feature/sections/ProcessSection';
import WhyChooseUs from '../../components/feature/sections/WhyChooseUs';
import ServiceAreas from '../../components/feature/sections/ServiceAreas';
import Testimonials from '../../components/feature/sections/Testimonials';
import FAQSection, { FAQContent } from '../../components/feature/sections/FAQSection';
import FinalCTA from '../../components/feature/sections/FinalCTA';

const heroContent: HeroContent = {
  label: 'Family-Owned Since Day One',
  headline: 'Building Beautiful Outdoor Spaces',
  headlineAccent: 'Across North Idaho',
  subheadline:
    'CQC Landscaping was founded on the belief that every property deserves a great outdoor space. We bring craftsmanship, integrity, and local expertise to every project we take on.',
  primaryCTA: { label: 'Get Free Estimate', href: '/#estimate' },
  secondaryCTA: { label: '(208) 659-6067', href: 'tel:+12086596067' },
  backgroundImage: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/02/sld-2.jpg',
  stats: [
    { value: '500+', label: 'Projects Delivered' },
    { value: '10+', label: 'Years Local Experience' },
    { value: '5.0★', label: 'Google Rated' },
  ],
};

const storyContent: ContentSectionData = {
  badge: 'Our Story',
  heading: 'Local Roots,',
  headingAccent: 'Lasting Results',
  items: [
    {
      title: 'How We Started',
      desc: "CQC Landscaping was built from the ground up in Hayden, ID with one mission: deliver reliable, high-quality landscaping solutions to local homeowners and businesses. Founded by Charles Clapper, CQC quickly earned a reputation for clean workmanship, honest communication, and projects delivered on schedule.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Custom-Landscaping.jpg',
      tag: 'Our Founding',
      href: '/#estimate',
    },
    {
      title: 'Our Commitment to Quality',
      desc: "Every CQC project is approached with the same attention to detail — whether it's a simple lawn installation or a full hardscape transformation. We source durable materials, stick to timelines, and treat your property with respect. Our work speaks for itself across hundreds of completed projects in North Idaho and Eastern Washington.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Hardscaping.jpg',
      tag: 'Our Standards',
      href: '/services/hardscaping',
    },
    {
      title: 'Serving Our Community',
      desc: "We're not just a landscaping company — we're your neighbors. CQC is rooted in Hayden, ID and deeply invested in the communities we serve across Post Falls, Coeur d'Alene, Rathdrum, Spokane WA, and the surrounding region. When you hire CQC, you're supporting a local, family-owned business.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Turf-Installation.jpg',
      tag: 'Community Focus',
      href: '/locations/colorado-springs',
    },
  ],
  layout: 'alternating',
};

const aiContent: AIAnswerContent = {
  badge: 'AI & Voice Search',
  heading: 'Who Is CQC Landscaping?',
  subheading: 'Clear answers for Google, AI assistants, and voice search.',
  answers: [
    {
      question: 'Who is CQC Landscaping?',
      answer:
        "CQC Landscaping is a family-owned, professional landscaping company based in Hayden, ID. Founded by Charles Clapper, CQC specializes in outdoor design, hardscaping, turf installation, drainage solutions, and seasonal services for residential and commercial clients throughout North Idaho and Spokane, WA.",
    },
    {
      question: 'What is CQC Landscaping\'s mission?',
      answer:
        "CQC Landscaping's mission is to deliver high-quality, durable outdoor spaces that increase property value and improve quality of life. They prioritize honest communication, clean workmanship, and on-time delivery for every project.",
    },
    {
      question: 'Is CQC Landscaping a local company?',
      answer:
        "Yes. CQC Landscaping is locally based in Hayden, ID and serves the surrounding North Idaho and Eastern Washington region. They are not a franchise or corporate chain — they are a family-owned business with deep roots in the Hayden community.",
    },
    {
      question: 'Why choose a professional landscaping company?',
      answer:
        'Professional landscapers like CQC ensure proper site assessment, material selection, drainage planning, and installation quality. This prevents costly problems down the road, increases curb appeal, and ensures your outdoor space is built to last in North Idaho\'s climate.',
    },
  ],
};

const processContent: ProcessContent = {
  badge: 'Our Approach',
  heading: 'How We Work With You',
  subheading: 'A clear, transparent process from first call to final walkthrough.',
  steps: [
    { number: '1', title: 'Free Consultation', desc: 'We visit your property at no cost, listen to your goals, and assess the site conditions.', icon: 'ri-chat-1-line' },
    { number: '2', title: 'Custom Design Plan', desc: 'We develop a detailed design tailored to your vision, property, and Idaho climate requirements.', icon: 'ri-draft-line' },
    { number: '3', title: 'Material Selection', desc: 'Together we choose premium materials designed for longevity and visual impact.', icon: 'ri-stack-line' },
    { number: '4', title: 'Professional Installation', desc: 'Our experienced, local crew installs your project with precision, care, and clean workmanship.', icon: 'ri-tools-line' },
    { number: '5', title: 'Final Walkthrough', desc: "We walk through the completed project together to ensure it meets your expectations — guaranteed.", icon: 'ri-checkbox-circle-line' },
  ],
};

const faqContent: FAQContent = {
  badge: 'FAQ',
  heading: 'About CQC Landscaping',
  items: [
    {
      q: 'Who founded CQC Landscaping?',
      a: "CQC Landscaping was founded by Charles Clapper in Hayden, ID. Built on a commitment to quality craftsmanship and honest service, the company has grown to serve hundreds of clients across North Idaho and Eastern Washington over more than a decade.",
    },
    {
      q: 'Is CQC Landscaping licensed and insured?',
      a: "Yes. CQC Landscaping is fully licensed and insured for residential and commercial landscaping and hardscaping in Idaho. Clients can request documentation at any time. We carry all required certifications for the work we perform.",
    },
    {
      q: 'What areas does CQC Landscaping serve?',
      a: "CQC Landscaping serves Hayden, Post Falls, Coeur d'Alene, Rathdrum, Athol, Spokane WA, and surrounding communities throughout North Idaho and Eastern Washington. We're based locally and committed to the region.",
    },
    {
      q: 'Does CQC offer commercial landscaping?',
      a: 'Yes. CQC Landscaping works with both residential and commercial clients. Commercial services include landscape installation, maintenance, hardscaping, drainage, and snow removal for businesses throughout North Idaho.',
    },
    {
      q: 'How do I get started with CQC Landscaping?',
      a: "Getting started is easy. Call or text us at (208) 659-6067, email info@cqc-landscaping.com, or submit the free estimate form on this website. We'll schedule a free on-site consultation within 1–2 business days.",
    },
  ],
};

export default function AboutPage() {
  return (
    <main>
      <HeroSection content={heroContent} />
      <TrustBar />
      <ContentSection content={storyContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs />
      <ServiceAreas />
      <Testimonials />
      <FAQSection content={faqContent} />
      <FinalCTA
        heading="Meet the CQC Team — Schedule a Free Visit"
        subheading="Get to know us in person. We'll come to your property, walk the site, and show you exactly what CQC Landscaping can do for you."
      />
    </main>
  );
}
